<svelte:options runes={false} />
<script>
  // Alerts tab (#3): the strategy's durable ALERT history (severity < Error — Info/Warning: divergence,
  // funds-exhausted, withdrawal, currency-mismatch, candle-retry …) — a timestamped Time/Severity/Code/Message
  // table, newest-first, that survives reloads/restarts. Mirrors the Errors tab exactly; it is the severity
  // COMPLEMENT of that feed. Authoritative history from the membership-gated, org-scoped REST read
  // (getStrategyAlerts); new alerts live-APPEND from the autotradeAlerts SSE store (this strategy + Info/Warning),
  // deduped by `seq`.
  import { onDestroy } from 'svelte';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { strategyAlerts, reloadStrategyAlerts, fmtErrorTime as fmtTime } from '../../../lib/strategyErrors.js';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  // The shared, deduped, severity-gated (Info/Warning), newest-first alerts source (REST history + live SSE).
  $: merged = $alertsStore;
  let alertsStore = strategyAlerts(null, null);
  let loading = false;
  let error = null;

  // (Re)bind the shared store and refresh on strategy/org change.
  let lastKey = '';
  $: {
    const key = `${strategyId}|${$selectedOrgId}`;
    if (key !== lastKey && strategyId) {
      lastKey = key;
      alertsStore = strategyAlerts(strategyId, $selectedOrgId);
      void refresh();
    }
  }

  async function refresh() {
    if (!strategyId) return;
    loading = true;
    error = null;
    const at = $selectedOrgId;
    try {
      await reloadStrategyAlerts(strategyId, at);
    } catch (err) {
      if ($selectedOrgId === at && strategy?.strategyId === strategyId) error = mapError(err);
    } finally {
      loading = false;
    }
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    if (msg.includes('status: 404')) return null; // unreadable/absent — treated as empty, not an error banner
    return "Couldn't load alert history — try reloading.";
  }

  // Fallback liveness poll for a running strategy (the SSE append is best-effort) — mirrors the Errors tab.
  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(refresh, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
</script>

<div class="at-errors-tab">
  <div class="at-errors-toolbar">
    {#if isRunning}
      <span class="at-live-badge" title="Updating live as alerts occur">● live</span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={refresh} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={refresh}>Retry</button>
    </div>
  {:else if loading && merged.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if merged.length === 0}
    <div class="empty-state">
      <p class="empty-state-description">No alerts recorded for this strategy.</p>
    </div>
  {:else}
    <div class="at-debug-table">
      <div class="at-debug-scroll">
        <table>
          <thead>
            <tr><th>Time</th><th>Severity</th><th>Code</th><th>Message</th></tr>
          </thead>
          <tbody>
            {#each merged as a (a.seq)}
              <tr>
                <td>{fmtTime(a.time)}</td>
                <td class="at-alert at-sev-{String(a.severity).toLowerCase()}">{a.severity}</td>
                <td>{a.code}</td>
                <td>{a.message}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  .at-errors-toolbar { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
  .at-live-badge { font-size: 0.75rem; color: var(--success, #1aa260); }
  .at-debug-table { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  .at-debug-scroll { max-height: 480px; overflow: auto; }
  .at-debug-table table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
  .at-debug-table th, .at-debug-table td { padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); text-align: left; vertical-align: top; }
  .at-debug-table th { position: sticky; top: 0; background: var(--surface-1, #14151a); }
  .at-debug-table td:last-child { white-space: normal; word-break: break-word; }
  .at-sev-warning { color: var(--warning, #d8a000); font-weight: 700; }
  .at-sev-info { color: var(--text-secondary, #b5b6b8); }
</style>
